import { Translate } from '../LanguageService';

// Api.checkStatus throws `new Error(response.statusText)` and hangs the untouched Response off the
// error, so the useful text — FluentValidation messages, the 409 "flag already exists" — only lives
// in the body. statusText is also always empty over HTTP/2, so without reading the body the admin
// gets nothing at all.
interface ApiError {
    message?: string;
    response?: Response;
}

interface DomainErrorBody {
    error?: {
        message?: string;
        shouldTranslate?: boolean;
    };
    errors?: Array<{ value?: string }>;
    exceptionMessage?: string;
}

// LanguageService.translate returns a red <span> element for an unknown key, not a string, despite
// Translate's `: string` signature. Callers interpolate this message into a translated string, so an
// element would surface as "[object Object]" — fall back to the raw key instead.
function translateToText(key: string): string {
    const translated = Translate(key);
    return typeof translated === 'string' ? translated : key;
}

function messageFromBody(body: DomainErrorBody): string | null {
    if (body == null) {
        return null;
    }

    if (body.error && body.error.message) {
        return body.error.shouldTranslate
            ? translateToText(body.error.message)
            : body.error.message;
    }

    if (body.errors && body.errors.length > 0) {
        const messages = body.errors
            .map((e) => (e ? e.value : null))
            .filter((m) => typeof m === 'string' && m.length > 0)
            .map((m) => translateToText(m as string));
        if (messages.length > 0) {
            return messages.join(' ');
        }
    }

    if (body.exceptionMessage) {
        return body.exceptionMessage;
    }

    return null;
}

/**
 * Best available text for a failed API request: the message the API actually sent, falling
 * back to the Error message and finally to a generic string. Never throws and never rejects.
 */
export async function readApiErrorMessage(error: unknown): Promise<string> {
    const apiError = (error != null ? error : {}) as ApiError;
    const response = apiError.response;

    if (response && typeof response.json === 'function') {
        try {
            // Clone so an already-consumed body (Api reads it for some statuses) still leaves the
            // original readable elsewhere, and vice versa.
            const source = typeof response.clone === 'function' ? response.clone() : response;
            const message = messageFromBody(await source.json());
            if (message) {
                return message;
            }
        } catch (e) {
            // Empty or non-JSON body — fall through to the status/generic text below.
        }
    }

    if (apiError.message) {
        return apiError.message;
    }

    return Translate('feature-flags/common/unknown-error');
}
